import { t } from '@lingui/core/macro';
import {
  type FacilityTransferItemPublicRecord,
  type FacilityTransferRoundPublicRecord,
  FacilityTransferDirection
} from '@navio/nsdk';
import { formatInTimeZone, NoddiDateFormats } from 'noddi-util';

const HALF_HOUR_MS = 30 * 60 * 1000;

const formatTime = (date: Date, timeZone: string) => formatInTimeZone(date, NoddiDateFormats.TIME, timeZone);

// Rounded down: wheel sets ready a little early beat a vehicle arriving to nothing ready.
const formatReadyByTime = (expectedArrivalAt: string, timeZone: string): string =>
  formatTime(new Date(Math.floor(new Date(expectedArrivalAt).getTime() / HALF_HOUR_MS) * HALF_HOUR_MS), timeZone);

// Deliveries carry no deadline for the recipient, so the nearest half hour is honest enough.
const formatAroundTime = (expectedArrivalAt: string, timeZone: string): string =>
  formatTime(new Date(Math.round(new Date(expectedArrivalAt).getTime() / HALF_HOUR_MS) * HALF_HOUR_MS), timeZone);

// Pickups show when things must be ready (rounded down); deliveries show roughly when they arrive.
const TIME_BY_DIRECTION: Record<FacilityTransferDirection, (expectedArrivalAt: string, timeZone: string) => string> = {
  [FacilityTransferDirection.OUTGOING]: formatReadyByTime,
  [FacilityTransferDirection.INCOMING]: formatAroundTime
};

export interface MergedRound {
  number: number;
  time: string;
  roundNumbers: number[];
  itemCount: number;
}

/**
 * Stops that land on the same displayed time are one pickup (or delivery) for the facility, even when
 * separate vehicles or orders make them. Rounds arrive sorted, and rounding is monotonic, so equal
 * times are always adjacent.
 */
export const mergeRoundsByTime = (
  rounds: FacilityTransferRoundPublicRecord[],
  direction: FacilityTransferDirection,
  /** The facility's: times are shown on its clock. */
  timeZone: string
): MergedRound[] => {
  const merged: MergedRound[] = [];
  for (const round of rounds) {
    const time = TIME_BY_DIRECTION[direction](round.expectedArrivalAt, timeZone);
    const last = merged.at(-1);
    if (last?.time === time) {
      last.roundNumbers.push(round.number);
      last.itemCount += round.itemCount;
    } else {
      merged.push({ number: merged.length + 1, time, roundNumbers: [round.number], itemCount: round.itemCount });
    }
  }
  return merged;
};

const ROUND_LABEL_BY_DIRECTION: Record<
  FacilityTransferDirection,
  (round: MergedRound, roundItems: FacilityTransferItemPublicRecord[]) => { title: string; timeLabel: string }
> = {
  [FacilityTransferDirection.OUTGOING]: ({ number: pickupNumber, time }) => ({
    title: t`Pickup ${pickupNumber}`,
    timeLabel: t`Ready by ${time}`
  }),
  [FacilityTransferDirection.INCOMING]: ({ number: deliveryNumber, time }, roundItems) => {
    const sources = [...new Set(roundItems.map((item) => item.counterpartFacilityName))].join(', ');
    return { title: t`Delivery ${deliveryNumber}`, timeLabel: t`Arrives around ${time} · from ${sources}` };
  }
};

export interface PickupRoundGroup {
  key: string;
  marker: string;
  title: string;
  timeLabel: string | null;
  items: FacilityTransferItemPublicRecord[];
}

export const groupItemsByRound = (
  items: FacilityTransferItemPublicRecord[],
  rounds: FacilityTransferRoundPublicRecord[],
  direction: FacilityTransferDirection,
  timeZone: string
): PickupRoundGroup[] =>
  [
    ...mergeRoundsByTime(rounds, direction, timeZone).map((round) => {
      const roundItems = items.filter(
        (item) => item.roundNumber != null && round.roundNumbers.includes(item.roundNumber)
      );
      return {
        key: `round-${round.number}`,
        marker: String(round.number),
        ...ROUND_LABEL_BY_DIRECTION[direction](round, roundItems),
        items: roundItems
      };
    }),
    {
      key: 'unplanned',
      marker: '–',
      title: t`Not planned yet`,
      timeLabel: null,
      items: items.filter((item) => item.roundNumber == null)
    }
  ].filter((group) => group.items.length > 0);

// Round order first; within a round, fleet owners' wheel sets are usually stored together.
export const compareByRoundThenOwnerThenPlate = (
  a: FacilityTransferItemPublicRecord,
  b: FacilityTransferItemPublicRecord
) =>
  (a.roundNumber ?? Number.MAX_SAFE_INTEGER) - (b.roundNumber ?? Number.MAX_SAFE_INTEGER) ||
  (a.ownerName ?? '').localeCompare(b.ownerName ?? '') ||
  (a.licensePlate ?? '').localeCompare(b.licensePlate ?? '');
